import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import styles from '../../views/ProductDetails/ProductDetails.module.scss';
import ultraStyles from './UltraPDP.module.scss';
import HideChatWidget from '../ZendeskChat/HideChatWidget';
import AppPaths from '../../AppPaths';
import Footer from '../Footer/Footer';
import NavBar from '../AppBar/NavBar';
import PlusMembershipValueProps from '../../views/ProductDetails/Series3CollarDetails/components/PlusMembershipValueProps';
import FAQ from '../../views/ProductDetails/Series3CollarDetails/components/FAQ';
import CollarAddToBagButton from '../../views/ProductDetails/Series3CollarDetails/components/CollarAddToBagButton';
import * as types from '../../types';
import { cartActions } from '../../reducers/storeCart';
import useSubscriptionProducts from '../../hooks/useSubscriptionProducts';
import { useLocale } from '../../contexts/LocaleContext';
import { CustomerIO } from '../../lib/analytics/customerIO';
import * as events from '../../lib/analytics/events';
import { formattedCentsToDollars } from '../../lib/util';
import { isInAppShop } from '../../lib/inAppShop';

import { ProductTitle, ProductTitleDescription } from './ProductTitle/ProductTitle';
import UltraMembershipOptions from './UltraMembershipOptions/UltraMembershipOptions';
import UltraProductSummary from './UltraProductSummary/UltraProductSummary';
import UltraAppCarousel from './UltraAppCarousel/UltraAppCarousel';
import UltraAllFeatures from './UltraAllFeatures/UltraAllFeatures';
import UltraDesktopImageGallery from './UltraDesktopImageGallery/UltraDesktopImageGallery';
import WhoAreYouShoppingFor, { BuyingFor } from './WhoAreYouShoppingFor/WhoAreYouShoppingFor';
import useUltraExperienceGuard from './useUltraExperienceGuard';
import useUltraEligiblePets from '../../hooks/useUltraEligiblePets';
import UltraConfirmationModal from '../../views/UltraUpgrade/UltraConfirmationModal';
import { usePdpImagesContext } from '../../contexts/PdpImagesContext';

export default function UltraPDP() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const products = useSelector((state: types.AppState) => state.config.products);
  const { currency } = useLocale();

  // Get pre-loaded dynamic images from context
  const { getMedia } = usePdpImagesContext();
  const dynamicImages = getMedia(types.ultraCollarId);

  const product = products.find((p) => p.id === types.ultraCollarId) as
    | (types.IUltraProduct & { sku: string })
    | undefined;

  const allUltraSubscriptions = useSubscriptionProducts(types.ultraCollarId);
  const subscriptions = allUltraSubscriptions.filter((p) => !p.thirdParty && p.renewalMonths !== 6);

  const initialSubscription = subscriptions.find((s) => s.renewalMonths === 12);
  const [selectedSubscription, setSelectedSubscription] = useState<types.ISubscriptionProduct | undefined>(
    initialSubscription,
  );

  const eligiblePets = useUltraEligiblePets();
  const showPicker = eligiblePets.length > 0;
  const [buyingFor, setBuyingFor] = useState<BuyingFor | null>(null);
  const [existingDogModalOpen, setExistingDogModalOpen] = useState(false);
  const firstEligiblePet = eligiblePets[0];

  useEffect(() => {
    if (showPicker) {
      events.ultraPDPShoppingForPickerShown({ eligiblePetCount: eligiblePets.length });
    }
  }, [showPicker, eligiblePets.length]);

  const onSelectBuyingFor = useCallback(
    (choice: BuyingFor) => {
      setBuyingFor(choice);
      events.ultraPDPShoppingForSelected({ choice, eligiblePetCount: eligiblePets.length });
    },
    [eligiblePets.length],
  );

  useEffect(() => {
    if (!selectedSubscription && subscriptions.length > 0) {
      const oneYear = subscriptions.find((s) => s.renewalMonths === 12);
      setSelectedSubscription(oneYear ?? subscriptions[0]);
    }
  }, [subscriptions, selectedSubscription]);

  const productSku = product?.sku ?? 'U1-MU';

  const isUltraSupportedInCountry = useUltraExperienceGuard();

  const addToBag = useCallback(() => {
    if (showPicker && buyingFor === 'existing') {
      setExistingDogModalOpen(true);
      return true;
    }

    if (!selectedSubscription) {
      return false;
    }

    dispatch(
      cartActions.addBundleCartItem({
        sku: productSku,
        subscriptionSku: selectedSubscription.sku,
        addonSkus: [],
        currency,
        isGift: false,
      }),
    );

    CustomerIO.trackAddedItemToCart(types.ultraCollarId);
    if (isInAppShop()) {
      dispatch(cartActions.setShowInAppCheckout(true));
    } else {
      history.push(AppPaths.Bag);
    }
    return true;
  }, [showPicker, buyingFor, dispatch, productSku, selectedSubscription, currency, history]);

  if (!isUltraSupportedInCountry || !product) {
    return null;
  }

  const isExistingDogPath = showPicker && buyingFor === 'existing';
  const isNewDogPath = !showPicker || buyingFor === 'new';
  const productPriceInCents = isExistingDogPath ? product.pricingInCents.USD : product.standalonePricingInCents.USD;

  const canAddToBag = isExistingDogPath || (isNewDogPath && !!selectedSubscription);

  const descriptionText = isExistingDogPath
    ? t('ultra_existing_dog_cart_description', { hardwarePrice: formattedCentsToDollars(productPriceInCents) })
    : isNewDogPath && selectedSubscription
      ? t('ultra_cart_description', {
          hardwarePrice: `${formattedCentsToDollars(productPriceInCents)}`,
          membershipPrice: `${formattedCentsToDollars(selectedSubscription.pricingInCents.USD)}`,
        })
      : null;

  return (
    <>
      <div
        className={classNames(styles.productContainer, styles.ecomFlowRevamp, 'ecom-flow-revamp', 'ultra-pdp', {
          'ecom-flow-revamp-light-v2': true,
        })}
      >
        <NavBar />
        <div className={classNames('image-gallery-container', styles.galleryContainer)}>
          <UltraDesktopImageGallery dynamicImages={dynamicImages} />
        </div>
        <div className={classNames(styles.productDetails, 'product-details')}>
          <div className={styles.productTitleContainer}>
            <ProductTitle />
            <ProductTitleDescription />
            {showPicker && (
              <WhoAreYouShoppingFor
                petNames={eligiblePets.map((p) => p.name)}
                selected={buyingFor}
                onSelect={onSelectBuyingFor}
              />
            )}
            <UltraMembershipOptions
              subscriptions={subscriptions}
              selectedSubscription={selectedSubscription}
              onSelectSubscription={setSelectedSubscription}
              disableSection={showPicker && buyingFor !== 'new'}
            />
          </div>
          <PlusMembershipValueProps />
          <CollarAddToBagButton
            ctaText={canAddToBag ? t('add_to_cart') : t('ultra_pick_your_membership')}
            descriptionText={descriptionText}
            isVisibleOverride={canAddToBag}
            onAddToBag={addToBag}
          />
        </div>
      </div>
      {firstEligiblePet && existingDogModalOpen && (
        <UltraConfirmationModal
          moduleId={firstEligiblePet.moduleId}
          initialPetChoice="existing"
          open
          onOpenChange={setExistingDogModalOpen}
        />
      )}
      <UltraProductSummary />
      <UltraAppCarousel />
      <UltraAllFeatures />
      <FAQ collarId={types.ultraCollarId} />
      <div className={ultraStyles.footerContainer}>
        <Footer />
      </div>
      <HideChatWidget />
    </>
  );
}
